import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get_automation_version_url } from '@/utils/urls.ts';
import {
  AUTOMATION_KEY,
  AUTOMATION_LATEST_VERSION_KEY,
  AUTOMATION_VERSION_KEY,
  AUTOMATION_VERSIONS_KEY,
} from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { AutomationVersion, PinnedOutput } from '@/types/automations.ts';

type UpdateAutomationVersionParams = {
  versionId: string;
  name?: string;
  pinned_steps?: string[];
  pinned_outputs?: PinnedOutput[];
};

const useUpdateAutomationVersion = (): UseMutationResult<
  AutomationVersion,
  AxiosError,
  UpdateAutomationVersionParams
> => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const automationId = useAutomationId();

  return useMutation({
    mutationFn: async ({
      versionId,
      name,
      pinned_steps,
      pinned_outputs,
    }: UpdateAutomationVersionParams) => {
      const response = await apiClient.put(get_automation_version_url(versionId), {
        name,
        pinned_steps,
        pinned_outputs,
      });
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSIONS_KEY(automationId) });
      await queryClient.invalidateQueries({
        queryKey: AUTOMATION_LATEST_VERSION_KEY(automationId),
      });
      await queryClient.invalidateQueries({
        queryKey: AUTOMATION_VERSION_KEY(automationId, versionId),
      });
      return response.data;
    },
  });
};

export default useUpdateAutomationVersion;
